import { ValidationError } from "../errors.js";
import type { HandlerContext, HandlerUtility } from "../types.js";

/**
 * Generic schema interface compatible with Zod and other validation libraries.
 */
export interface Schema<T> {
    parse: (data: unknown) => T;
    safeParse: (data: unknown) => { success: true; data: T } | { success: false; error: unknown };
}

/**
 * Options for the validation utility.
 */
export interface ValidateOptions<T> {
    /** Schema to validate against (Zod schema or compatible) */
    schema: Schema<T>;
    /** Source of data to validate */
    source?: "body" | "query" | "params";
    /** Custom error formatter */
    formatError?: (error: unknown) => unknown;
}

/**
 * Validation utility using Zod or compatible schema libraries.
 *
 * @example
 * ```ts
 * import { compose, withValidation } from "next-route-compose";
 * import { z } from "zod";
 *
 * const CreateUserSchema = z.object({
 *   name: z.string().min(1),
 *   email: z.string().email(),
 * });
 *
 * export const POST = compose(
 *   withValidation({ schema: CreateUserSchema })
 * )(async (ctx) => {
 *   // ctx.body is typed as { name: string; email: string }
 *   const { name, email } = ctx.body;
 *   return Response.json({ user: { name, email } });
 * });
 * ```
 */
export function withValidation<T>(options: ValidateOptions<T>): HandlerUtility {
    const { schema, source = "body", formatError } = options;

    return (handler) => {
        return async (context: HandlerContext) => {
            let data: unknown;

            // Get data from source
            switch (source) {
                case "body": {
                    try {
                        data = await context.request.json();
                    } catch {
                        return new ValidationError("Invalid JSON body").toResponse();
                    }
                    break;
                }
                case "query": {
                    const url = new URL(context.request.url);
                    data = Object.fromEntries(url.searchParams);
                    break;
                }
                case "params": {
                    // Params would typically come from Next.js route params
                    data = context["params"] ?? {};
                    break;
                }
            }

            // Validate data
            const result = schema.safeParse(data);

            if (!result.success) {
                const details = formatError ? formatError(result.error) : formatZodError(result.error);
                return new ValidationError("Validation failed", details).toResponse();
            }

            // Add validated data to context
            const enrichedContext: HandlerContext = {
                ...context,
                body: result.data,
            };

            return await handler(enrichedContext);
        };
    };
}

/**
 * Format Zod-like errors into a readable structure.
 */
function formatZodError(error: unknown): Record<string, string[]> {
    // Check if it's a Zod-like error with issues
    if (
        error &&
        typeof error === "object" &&
        "issues" in error &&
        Array.isArray((error as { issues: unknown[] }).issues)
    ) {
        const issues = (error as { issues: Array<{ path: (string | number)[]; message: string }> })
            .issues;
        const formatted: Record<string, string[]> = {};

        for (const issue of issues) {
            const path = issue.path.join(".") || "_root";
            if (!formatted[path]) {
                formatted[path] = [];
            }
            formatted[path].push(issue.message);
        }

        return formatted;
    }

    // Fallback for other error types
    return { _error: [String(error)] };
}

/**
 * Validate multiple sources at once.
 *
 * @example
 * ```ts
 * export const POST = compose(
 *   withMultiValidation({
 *     body: CreateUserSchema,
 *     query: PaginationSchema,
 *   })
 * )(handler);
 * ```
 */
export function withMultiValidation(schemas: {
    body?: Schema<unknown>;
    query?: Schema<unknown>;
    params?: Schema<unknown>;
}): HandlerUtility {
    return (handler) => {
        return async (context: HandlerContext) => {
            const validated: Record<string, unknown> = {};
            const errors: Record<string, unknown> = {};

            // Validate body
            if (schemas.body) {
                try {
                    const data = await context.request.clone().json();
                    const result = schemas.body.safeParse(data);
                    if (result.success) {
                        validated["body"] = result.data;
                    } else {
                        errors["body"] = formatZodError(result.error);
                    }
                } catch {
                    errors["body"] = { _error: ["Invalid JSON body"] };
                }
            }

            // Validate query
            if (schemas.query) {
                const url = new URL(context.request.url);
                const data = Object.fromEntries(url.searchParams);
                const result = schemas.query.safeParse(data);
                if (result.success) {
                    validated["query"] = result.data;
                } else {
                    errors["query"] = formatZodError(result.error);
                }
            }

            // Validate params
            if (schemas.params && context["params"]) {
                const result = schemas.params.safeParse(context["params"]);
                if (result.success) {
                    validated["params"] = result.data;
                } else {
                    errors["params"] = formatZodError(result.error);
                }
            }

            // If any errors, return validation error
            if (Object.keys(errors).length > 0) {
                return new ValidationError("Validation failed", errors).toResponse();
            }

            // Add validated data to context
            const enrichedContext: HandlerContext = {
                ...context,
                ...validated,
            };

            return await handler(enrichedContext);
        };
    };
}
